import { IBaseAction } from '@models/actions/IBaseAction';

/**
 * Настройка пункта меню «Создать документ» из конфига действия
 * (DocumentActionsReact.xml → ClientOptions → menu). Все поля опциональны:
 * действие без настройки попадает в раздел «Прочее» с дефолтной иконкой.
 * Метаданные раздела (иконка/цвет/порядок) достаточно задать на одном
 * действии раздела — побеждает первое определение.
 */
export interface IActionMenuOptions {
    section?: string;
    sectionIcon?: string;
    sectionColor?: string;
    sectionOrder?: number;
    icon?: string;
    description?: string;
    order?: number;
}

export interface ICreateDocMenuItem {
    action: IBaseAction;
    icon?: string;
    description?: string;
    /** Название + описание в нижнем регистре — по этой строке ищет поиск меню. */
    searchText: string;
}

export interface ICreateDocMenuSection {
    name: string;
    icon?: string;
    color: string;
    items: ICreateDocMenuItem[];
}

export const OTHER_SECTION_NAME = 'Прочее';

/** Оттенки разделов, не задавших sectionColor, — по кругу в порядке разделов. */
const FALLBACK_SECTION_COLORS = ['#379cff', '#2ec3a0', '#f0a72e', '#a78bfa', '#4ca866', '#9aa0aa'];

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

interface ISectionDraft {
    name: string;
    icon?: string;
    color?: string;
    order?: number;
    firstIndex: number;
    items: (ICreateDocMenuItem & { order?: number; index: number })[];
}

const readMenuOptions = (action: IBaseAction): IActionMenuOptions => {
    const menu = action.options?.menu;
    return menu && typeof menu === 'object' ? (menu as IActionMenuOptions) : {};
};

const bySortKey = (orderA: number | undefined, orderB: number | undefined, indexA: number, indexB: number): number => {
    const a = typeof orderA === 'number' ? orderA : Number.MAX_SAFE_INTEGER;
    const b = typeof orderB === 'number' ? orderB : Number.MAX_SAFE_INTEGER;
    return a !== b ? a - b : indexA - indexB;
};

/**
 * Группирует действия PlanningMenu в разделы меню «Создать документ».
 * Порядок разделов: sectionOrder из конфига, затем порядок появления;
 * «Прочее» (действия без menu.section) — всегда последним.
 */
export const buildMenuSections = (actions: IBaseAction[]): ICreateDocMenuSection[] => {
    const drafts = new Map<string, ISectionDraft>();

    actions.forEach((action, index) => {
        const menu = readMenuOptions(action);
        const name = typeof menu.section === 'string' && menu.section.trim() ? menu.section.trim() : OTHER_SECTION_NAME;

        let draft = drafts.get(name);
        if (!draft) {
            draft = { name, firstIndex: index, items: [] };
            drafts.set(name, draft);
        }
        draft.icon = draft.icon ?? menu.sectionIcon;
        draft.color = draft.color ?? menu.sectionColor;
        draft.order = draft.order ?? menu.sectionOrder;

        const description = typeof menu.description === 'string' ? menu.description : undefined;
        draft.items.push({
            action,
            icon: typeof menu.icon === 'string' ? menu.icon : undefined,
            description,
            searchText: `${action.displayName} ${description ?? ''}`.toLowerCase(),
            order: menu.order,
            index,
        });
    });

    const sorted = [...drafts.values()].sort((a, b) => {
        if (a.name === OTHER_SECTION_NAME !== (b.name === OTHER_SECTION_NAME)) {
            return a.name === OTHER_SECTION_NAME ? 1 : -1;
        }
        return bySortKey(a.order, b.order, a.firstIndex, b.firstIndex);
    });

    return sorted.map((draft, sectionIndex) => ({
        name: draft.name,
        icon: draft.icon,
        color:
            draft.color && HEX_COLOR_RE.test(draft.color)
                ? draft.color
                : FALLBACK_SECTION_COLORS[sectionIndex % FALLBACK_SECTION_COLORS.length],
        items: draft.items
            .sort((a, b) => bySortKey(a.order, b.order, a.index, b.index))
            .map((item) => ({
                action: item.action,
                icon: item.icon,
                description: item.description,
                searchText: item.searchText,
            })),
    }));
};
